import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Bot, Zap, MessageCircle, TrendingUp, AlertCircle } from 'lucide-react';
import { AIFriendAnalysis } from '../../services/analyticsApi';

interface AIFriendEffectivenessCardProps {
  analysis: AIFriendAnalysis | undefined;
}

export const AIFriendEffectivenessCard: React.FC<AIFriendEffectivenessCardProps> = ({ analysis }) => {
  if (!analysis) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Bot className="h-5 w-5 text-purple-600" />
            AI Friend Effectiveness
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500">AI Friend not involved in this conversation</p>
        </CardContent>
      </Card>
    );
  }

  const getEffectivenessColor = (score: number) => {
    if (score >= 0.8) return 'text-green-600';
    if (score >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getToneIcon = (tone: string) => {
    const icons: Record<string, JSX.Element> = {
      professional: <MessageCircle className="h-4 w-4" />,
      friendly: <Zap className="h-4 w-4" />,
      informative: <MessageCircle className="h-4 w-4" />,
      encouraging: <TrendingUp className="h-4 w-4" />
    };
    return icons[tone.toLowerCase()] || <MessageCircle className="h-4 w-4" />;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Bot className="h-5 w-5 text-purple-600" />
          AI Friend Effectiveness
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* AI Friend Name */}
        <div className="flex items-center justify-between p-2 bg-purple-50 rounded border border-purple-100">
          <div className="flex items-center gap-2">
            <Bot className="h-4 w-4 text-purple-600" />
            <span className="text-sm text-gray-700">AI Friend</span>
          </div>
          <span className="font-medium text-purple-700">{analysis.ai_friend_name}</span>
        </div>

        {/* Overall Effectiveness */}
        <div className="flex items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-100">
          <div>
            <p className="text-sm text-gray-600">Effectiveness Score</p>
            <p className={`text-2xl font-bold ${getEffectivenessColor(analysis.effectiveness_score)}`}>
              {(analysis.effectiveness_score * 100).toFixed(0)}%
            </p>
          </div>
          {analysis.effectiveness_score >= 0.8 ? (
            <Badge variant="default">Excellent</Badge>
          ) : analysis.effectiveness_score >= 0.6 ? (
            <Badge variant="secondary">Good</Badge>
          ) : (
            <Badge variant="destructive">Needs Improvement</Badge>
          )}
        </div>

        {/* Naturalness Score */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-700">Naturalness</span>
            <span className="font-mono text-xs text-gray-500">
              {(analysis.naturalness_score * 100).toFixed(0)}%
            </span>
          </div>
          <Progress value={analysis.naturalness_score * 100} className="h-2" />
        </div>

        {/* Intervention Quality */}
        <div className="flex items-center justify-between p-2 bg-gray-50 rounded">
          <span className="text-sm text-gray-700">Intervention Quality</span>
          <Badge variant="outline" className="capitalize">{analysis.intervention_quality}</Badge>
        </div>

        {/* Suggestions */}
        {analysis.suggestions && analysis.suggestions.length > 0 && (
          <div className="pt-3 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Suggestions for Improvement</h4>
            <div className="space-y-1">
              {analysis.suggestions.map((suggestion, idx) => (
                <div key={idx} className="text-xs text-gray-600 flex items-start gap-1">
                  <span className="text-purple-600">•</span>
                  <span>{suggestion}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Low Effectiveness Warning */}
        {analysis.effectiveness_score < 0.5 && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-red-800">
              <p className="font-medium">Low AI Effectiveness</p>
              <p className="mt-1">Consider reviewing AI Friend prompts or intervention timing.</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

